/**
 * Makes a scrollable region keyboard-accessible (WCAG 2.1.1, Safari).
 *
 * @param {HTMLElement} element
 * @param {Object} [options]
 * @param {'horizontal' | 'vertical'} [options.axis='horizontal']
 * @param {number} [options.step=80]
 * @param {() => number} [options.getStep]
 */
export function bindScrollableRegionKeyboard(element, options = {}) {
  if (!(element instanceof HTMLElement) || element.dataset.scrollKeyboard === 'true') {
    return;
  }

  element.dataset.scrollKeyboard = 'true';

  if (!element.hasAttribute('tabindex')) {
    element.setAttribute('tabindex', '0');
  }

  const axis = options.axis ?? 'horizontal';
  const isHorizontal = axis === 'horizontal';

  const resolveStep = () => {
    const dynamicStep = options.getStep?.();
    if (typeof dynamicStep === 'number' && dynamicStep > 0) {
      return dynamicStep;
    }

    return options.step ?? 80;
  };

  element.addEventListener('keydown', (event) => {
    const step = resolveStep();
    let delta = 0;

    switch (event.key) {
      case 'ArrowLeft':
        delta = isHorizontal ? -step : 0;
        break;
      case 'ArrowRight':
        delta = isHorizontal ? step : 0;
        break;
      case 'ArrowUp':
        delta = isHorizontal ? 0 : -step;
        break;
      case 'ArrowDown':
        delta = isHorizontal ? 0 : step;
        break;
      case 'Home':
        event.preventDefault();
        if (isHorizontal) {
          element.scrollLeft = 0;
        } else {
          element.scrollTop = 0;
        }
        return;
      case 'End':
        event.preventDefault();
        if (isHorizontal) {
          element.scrollLeft = element.scrollWidth;
        } else {
          element.scrollTop = element.scrollHeight;
        }
        return;
      default:
        return;
    }

    if (delta === 0) return;

    event.preventDefault();
    if (isHorizontal) {
      element.scrollLeft += delta;
    } else {
      element.scrollTop += delta;
    }
  });
}
